import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Share2, Edit2, ExternalLink, MapPin, ArrowRight, Camera, FileText, LayoutGrid, Link2 } from 'lucide-react';
export function DashboardHome() {
  const profileUrl = 'eaves.pro/marcuschen';
  const fullUrl = `https://${profileUrl}`;
  // Simulated profile completeness — in production these come from the agent's data
  const profileItems = [{
    key: 'photo',
    label: 'Add a headshot',
    description: 'A real photo builds trust with sellers.',
    icon: Camera,
    complete: false,
    editAnchor: '/admin/edit#photo'
  }, {
    key: 'bio',
    label: 'Write your bio',
    description: "We've drafted one from your data — review or replace it.",
    icon: FileText,
    complete: false,
    editAnchor: '/admin/edit#bio'
  }, {
    key: 'listings',
    label: 'Feature your listings',
    description: 'Showcase active or upcoming properties.',
    icon: LayoutGrid,
    complete: true,
    editAnchor: '/admin/edit#sections'
  }, {
    key: 'links',
    label: 'Add custom links',
    description: 'Booking pages, intake forms, resources — anything you want.',
    icon: Link2,
    complete: false,
    editAnchor: '/admin/edit#sections'
  }];
  const incompleteItems = profileItems.filter(item => !item.complete);
  return <div className="p-8 max-w-4xl mx-auto" data-id="element-681">
      <h1 className="text-3xl font-serif font-semibold text-brand-dark mb-8" data-id="element-682">
        My Profile
      </h1>

      {/* Profile Preview Card */}
      <div className="bg-white rounded-2xl border border-brand-light shadow-sm overflow-hidden mb-8" data-id="element-683">
        <div className="p-6 bg-brand-light/30 flex flex-col items-center justify-center min-h-[300px]" data-id="element-684">
          {/* Mini Profile Preview */}
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-premium overflow-hidden border border-brand-light" data-id="element-685">
            <div className="h-24 bg-gradient-to-r from-brand-sage via-brand-sandstoneBg to-brand-light relative" data-id="element-686">
              <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-brand-forest to-transparent" data-id="element-687"></div>
            </div>
            <div className="px-6 pb-6 -mt-12 relative z-10 flex flex-col items-center text-center" data-id="element-688">
              <div className="w-20 h-20 rounded-full border-4 border-white overflow-hidden shadow-sm mb-3 bg-white" data-id="element-689">
                <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop" alt="Marcus Chen" className="w-full h-full object-cover" data-id="element-690" />
              </div>
              <h3 className="text-lg font-serif font-semibold text-brand-dark mb-1" data-id="element-691">
                Marcus Chen
              </h3>
              <p className="text-xs text-brand-muted font-serif italic mb-3" data-id="element-692">
                Senior real estate advisor
              </p>

              <div className="flex flex-col items-center gap-2 w-full" data-id="element-693">
                <div className="flex gap-2" data-id="element-694">
                  <span className="text-[10px] font-medium bg-brand-offwhite border border-brand-light px-2 py-0.5 rounded-full text-brand-dark" data-id="element-695">
                    Zillow 4.9 ★
                  </span>
                  <span className="text-[10px] font-medium bg-brand-offwhite border border-brand-light px-2 py-0.5 rounded-full text-brand-dark" data-id="element-696">
                    Google 4.8 ★
                  </span>
                </div>
                <p className="text-[10px] text-brand-muted font-mono mt-1" data-id="element-697">
                  94109 · 94123 · 94117
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-brand-light" data-id="element-698">
          <div data-id="element-699">
            <h2 className="text-lg font-medium text-brand-dark" data-id="element-700">Marcus Chen</h2>
            <a href="/marcuschen" target="_blank" rel="noreferrer" className="text-sm text-brand-muted font-mono hover:text-brand-forest transition-colors" data-id="element-701">
              {profileUrl}
            </a>
          </div>
          <div className="flex items-center gap-3" data-id="element-702">
            <a href="/marcuschen" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-brand-forest hover:bg-brand-forestHover text-white rounded-xl text-sm font-medium transition-colors shadow-sm" data-id="element-703">
              Preview
              <ExternalLink size={16} data-id="element-704" />
            </a>
            <Link to="/admin/share" className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-brand-light hover:border-brand-forest/30 rounded-xl text-sm font-medium text-brand-dark transition-colors shadow-sm" data-id="element-705">
              <Share2 size={16} className="text-brand-muted" data-id="element-706" />
              Share
            </Link>
            <Link to="/admin/edit" className="inline-flex items-center gap-2 px-4 py-2 bg-brand-offwhite border border-brand-light hover:bg-brand-sage rounded-xl text-sm font-medium text-brand-dark transition-colors" data-id="element-707">
              <Edit2 size={16} className="text-brand-muted" data-id="element-708" />
              Edit
            </Link>
          </div>
        </div>
      </div>

      {/* Make it yours — only shows incomplete items as deep-link nudges */}
      {incompleteItems.length > 0 && <div className="bg-white rounded-2xl border border-brand-light shadow-sm p-6 mb-6" data-id="element-709">
          <h2 className="text-xl font-serif font-medium text-brand-dark" data-id="element-710">
            Make it yours
          </h2>
          <p className="text-sm text-brand-muted mt-1 mb-5" data-id="element-711">
            The fundamentals are already set — your performance, reviews, and
            verification. These personal touches are yours to add.
          </p>

          <div className="space-y-1" data-id="element-712">
            {incompleteItems.map(item => {
          const Icon = item.icon;
          return <Link key={item.key} to={item.editAnchor} className="flex items-center gap-4 px-4 py-3.5 rounded-xl hover:bg-brand-offwhite transition-colors group" data-id="element-713">
                  <div className="w-8 h-8 rounded-lg bg-brand-offwhite border border-brand-light flex items-center justify-center shrink-0 group-hover:bg-brand-sage group-hover:border-brand-forest/20 transition-colors" data-id="element-714">
                    <Icon size={16} className="text-brand-muted group-hover:text-brand-forest transition-colors" data-id="element-715" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-716">
                    <span className="text-sm font-medium text-brand-dark" data-id="element-717">
                      {item.label}
                    </span>
                    <span className="text-sm text-brand-muted ml-2" data-id="element-718">
                      {item.description}
                    </span>
                  </div>
                  <ArrowRight size={16} className="text-brand-light group-hover:text-brand-forest shrink-0 transition-colors" data-id="element-719" />
                </Link>;
        })}
          </div>
        </div>}

      {/* Market Status Section */}
      <div className="bg-brand-sage/30 rounded-2xl border border-brand-sage p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-id="element-720">
        <div className="flex items-center gap-4" data-id="element-721">
          <div className="w-10 h-10 rounded-full bg-brand-sage flex items-center justify-center text-brand-forest shrink-0" data-id="element-722">
            <MapPin size={20} data-id="element-723" />
          </div>
          <div data-id="element-724">
            <h3 className="font-medium text-brand-dark" data-id="element-725">Market Status</h3>
            <p className="text-sm text-brand-muted mt-0.5" data-id="element-726">
              We will match sellers and buyers to you.
            </p>
          </div>
        </div>
        <a href="#" className="text-sm text-brand-forest font-medium hover:underline whitespace-nowrap" data-id="element-727">
          Learn details and help activate your market →
        </a>
      </div>
    </div>;
}